/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {useMemo, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import Chip from '../components/chip/Chip';
import {useCurrentLayerChipClasses} from '../maps/layers/currentPanelStyles';
import {dlsAlt, dlsColor} from '../theme/theme';
import useThemeContext from '../theme/useThemeContext';
import {LabelContentType} from '../types/types';
import {getInitialSelectedLabels} from './helpers';

export interface LabelChipChooserProps {
  value: LabelContentType;
  label: string;
  onChange?: Function;
  disabled?: boolean;
  entityLabel: string;
};

const useStyles = makeStyles(() => ({
  chipContainer : {
    marginLeft : '16px'
  },
  wrapper : {
    display  : 'flex',
    flexWrap : 'wrap'
  },
  label : {
    ...dlsAlt('maps', 'layer', 'listHeader'),
    color      : ({disabled}: {disabled: boolean}) => disabled ? dlsColor('grey4') : dlsColor('grey3'),
    fontSize   : '16px',
    padding    : '4px 4px 6px',
    fontWeight : 400
  }
}));

/**
 * @LabelChipChooser
 * The LabelChipChooser component is a component that allows the user to select
 * a primary and secondary label for an entity.
 *
 * ```typescript
 * import {LabelChipChooser} from '@ion/dls/src/editors/LabelChipChooser';
 *
 * const labelContent = {primaryLabel: 'assetName', secondaryLabel: 'assetId'};
 *
 * <LabelChipChooser label={'Choose Label'}
 *  entityLabel={'assetName'}
 *  onChange={handleLabelChange}
 *  disabled={false}
 *  value={labelContent} />
 *
 * ```
 *
 * @param {string} label - label for the component
 * @param {value} value - the value of the labels
 * @param {Function} onChange - callback function to update the labels when the value changes
 * @param {boolean} disabled - whether the component is disabled
 * @param {string} entityLabel - the label of the entity
 */
export const LabelChipChooser = ({
  label,
  value,
  onChange,
  disabled,
  entityLabel = 'assetName'
}: LabelChipChooserProps) => {
  const classes                             = useStyles({disabled});
  const {isCurrent}                         = useThemeContext();
  const [selectedLabels, setSelectedLabels] = useState<string[]>(getInitialSelectedLabels(value));

  // the current theme's chip classes - legacy keeps the shared Chip's own
  // styling ...
  const getCurrentChipClasses = useCurrentLayerChipClasses(isCurrent);

  const handleChipClick = (chipLabel: string) => {
    let currentLabels = selectedLabels.filter(label => label !== null);
    if (currentLabels.includes(chipLabel)) {
      currentLabels = currentLabels.filter(label => label !== chipLabel);
    } else {
      if (currentLabels.length === 0) {
        currentLabels = [chipLabel];
      } else if (currentLabels.length === 1) {
        currentLabels = [...currentLabels, chipLabel];
      } else if (currentLabels.length === 2) {
        currentLabels = [currentLabels[0], chipLabel];
      }
    }
    // Filter out any null values again to clean up before setting the state.
    currentLabels = currentLabels.filter(label => label !== null);
    setSelectedLabels(currentLabels);
    const primary = currentLabels[0] || null;
    const secondary = currentLabels[1] || null;
    const labels = {
      primaryLabel   : primary,
      secondaryLabel : secondary
    };
    onChange?.(labels);
  };

  const labelOptions = useMemo(() => (
    translate(`common:editors.${entityLabel}`, {returnObjects: true})
  ), []);

  return (
    <div className={classes.chipContainer}>
      <div className={classes.label}>
        {label}
      </div>
      <div className={classes.wrapper}>
        {labelOptions.map(option => {
          const {label, value} = option;
          const isSelected     = selectedLabels.includes(value);
          return (
            <Chip key={value}
              classes={getCurrentChipClasses(isSelected)}
              textTransform={'uppercase'}
              disabled={disabled}
              style={{marginBottom: 10}}
              showCheckmark={selectedLabels[0] === value}
              isSelected={isSelected}
              onDelete={function() { handleChipClick(value); }}
              onClick={function() { handleChipClick(value); }}
              variant={'outlined'}
              label={label} />
          );
        })}
      </div>
    </div>
  );
};

export default LabelChipChooser;
